import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  AspectRatio,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Image,
  Stack,
} from '@chakra-ui/react';
import { RecipeTagsQuery } from '@diamond/shared/types';
import { Breadcrumbs, Heading } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { SEONext } from '@diamond/sol-buyer/seo-next';
import { useRouteQuery } from '@diamond/sol-buyer/utils';
import {
  recipeTagsOptions,
  useAuthStore,
  useGetRecipeListSection,
  useGetRecipeTags,
  useGetRecipeTagsCount,
} from '@diamond/sol-buyer-context';
import { ChevronLeft } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { BleedBackground } from '../layouts';
import { FavoriteRecipeIcon } from './icons';
import { InfiniteScrollRecipeList } from './InfiniteScrollRecipeList';
import { RecipeFilterMobile } from './RecipeFilterMobile';
import { RecipeList } from './RecipeList';
import { RecipeListAutocomplete } from './RecipeListAutocomplete';
import { RecipeListFilterCategory } from './RecipeListFilterCategory';
import { RecipeListTabs } from './RecipeListTabs';

interface RecipeContentProps {
  isAuthenticated: boolean;
  isMobile: boolean;
  withAuth: (callback: () => void, path: string) => Promise<void>;
}

export const RecipeContent = ({
  isAuthenticated,
  isMobile,
  withAuth,
}: RecipeContentProps) => {
  const router = useRouter();
  const routeQuery = useRouteQuery();
  const keyword = routeQuery?.['q'] as string | undefined;
  const category = routeQuery?.['category'] as RecipeTagsQuery['category'];
  const tags = routeQuery?.['tags'] as string[];
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.access_token);

  const notFiltering = !keyword && !category && !tags;

  const {
    data: recipeTagsCount,
    isError: isTagsCountError,
    error: tagsCountError,
    refetch: refetchTagsCount,
  } = useGetRecipeTagsCount();
  const {
    data: recipeSection,
    isError: isSectionError,
    error: sectionError,
    refetch: refetchSection,
  } = useGetRecipeListSection();
  const {
    data: recipeTags,
    isPending: isRecipeTagsPending,
    isError: isTagsError,
    error: tagsError,
    refetch: refetchTags,
  } = useGetRecipeTags({ category });

  const tabs = useMemo(() => {
    const tabs = [
      { title: `Semua (${recipeTagsCount?.all || 0})`, value: '' },
      { title: `Makanan (${recipeTagsCount?.food || 0})`, value: 'food' },
      {
        title: `Minuman (${recipeTagsCount?.beverage || 0})`,
        value: 'beverage',
      },
    ];
    return tabs;
  }, [recipeTagsCount?.all, recipeTagsCount?.beverage, recipeTagsCount?.food]);

  const handleFilterTagsByCategory = async (value: string) => {
    const tags = routeQuery?.['tags'] as string[];

    const serverTags = await queryClient.ensureQueryData(
      recipeTagsOptions(token, {
        category: (value || undefined) as RecipeTagsQuery['category'],
      })
    );

    let nextTags: string[] = tags;

    if (tags) {
      const arrTags: string[] = Array.isArray(tags) ? tags : [tags];
      const diff = arrTags.filter((id) =>
        serverTags.data.some((nextTag) => nextTag.id === id)
      );
      nextTags = diff;
    }

    return nextTags;
  };

  return (
    <Box pos="relative" pb={{ base: 'unset', md: '12' }}>
      <SEONext title="Recipes" />
      <BleedBackground
        bgImage="url('/images/recipe-pattern.png')"
        bgRepeat="repeat"
        bgSize="12px"
        bgColor="#FEF9F2"
        backgroundBlendMode="luminosity"
        isMainBg
      />
      {(isSectionError || isTagsError || isTagsCountError) && (
        <Alert status="error" variant="subtle" mb={4} rounded="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize={{ base: 'sm', md: 'md' }}>
              Gagal memuat data resep
            </AlertTitle>
            <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
              {String(
                (sectionError as Error)?.message ||
                  (tagsError as Error)?.message ||
                  (tagsCountError as Error)?.message ||
                  ''
              )}
            </AlertDescription>
          </Box>
          <Flex ml="auto" gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                refetchSection();
                refetchTags();
                refetchTagsCount();
              }}
            >
              Coba lagi
            </Button>
          </Flex>
        </Alert>
      )}
      {!isAuthenticated && (
        <Box mb={{ md: 8 }}>
          <AspectRatio ratio={1302 / 320} h="full" w="full">
            <Image
              rounded="md"
              w="100%"
              objectFit="fill"
              alt="logo"
              src="/images/public-banner-dummy.png"
            />
          </AspectRatio>
        </Box>
      )}
      <Box display={{ base: 'none', md: 'block' }}>
        <Breadcrumbs
          links={[
            { link: '/', title: 'Beranda' },
            { link: '/recipes', title: 'Resep Chef Sukanda Djaya' },
          ]}
        />
        <Flex justifyContent="space-between" alignItems="center" mt="8">
          <Flex display={{ base: 'none', md: 'flex' }} align="center">
            <IconButton
              fontSize="45px"
              name="back-icon"
              icon={<ChevronLeft fontSize="inherit" />}
              variant="unstyled"
              aria-label="Go back"
              display="flex"
              alignItems="center"
              onClick={() => router.back()}
            />
            <Heading fontWeight="light" fontSize={{ base: 'md', md: '3xl' }}>
              Resep Chef Sukanda Djaya
            </Heading>
          </Flex>
          <Button
            leftIcon={<FavoriteRecipeIcon fontSize="24px" />}
            variant="outline"
            bg="white"
            onClick={async () =>
              await withAuth(() => {
                trackEvent(TRACKING_EVENTS.ADD_TO_FAV_RECIPE, {
                  section_name: TRACKING_PAGES.RECIPE,
                });
                router.push('/recipes/favorite');
              }, 'recipes')
            }
          >
            Resep Favorit
          </Button>
        </Flex>
      </Box>
      <Grid
        templateColumns={{ base: '100%', lg: 'repeat(24, minmax(0, 1fr))' }}
        mt={{ base: '0', md: '8' }}
        gap={{ base: 4, md: 6 }}
      >
        {/* Tabs */}
        <GridItem colSpan={{ base: 1, lg: 15 }}>
          <RecipeListTabs
            currentCategory={category}
            tabs={tabs}
            onTabMouseEnter={(value) => {
              queryClient.prefetchQuery(
                recipeTagsOptions(token, {
                  category: (value || undefined) as RecipeTagsQuery['category'],
                })
              );
            }}
            onTabChange={async (value) => {
              trackEvent(`click_filter_recipe_category`, {
                category: value,
                section_name: 'recipe_list',
              });
              const tags = await handleFilterTagsByCategory(value);
              router.replace(
                {
                  pathname: router.pathname,
                  query: cleanDeep({
                    ...routeQuery,
                    tags,
                    category: value,
                  }),
                },
                undefined,
                {
                  shallow: true,
                }
              );
            }}
          />
        </GridItem>

        {/* Search Bar */}
        <GridItem
          px={{ base: 2, md: 0 }}
          colSpan={{ base: 1, lg: 9 }}
          zIndex="1"
        >
          <RecipeListAutocomplete
            q={keyword || ''}
            rightButton={(handleClick) =>
              isMobile && !isRecipeTagsPending && recipeTags ? (
                <RecipeFilterMobile categories={recipeTags} />
              ) : (
                <Button
                  variant="outline"
                  px="6"
                  bg="white"
                  onClick={() => {
                    handleClick();
                    trackEvent(TRACKING_EVENTS.SEARCH_RECIPE, {
                      keyword,
                      section_name: TRACKING_PAGES.RECIPE,
                    });
                  }}
                >
                  Cari Resep
                </Button>
              )
            }
          />
        </GridItem>

        {/* Category Filter */}
        {!keyword && (
          <GridItem px={{ base: 2, md: 0 }} colSpan={{ base: 1, lg: 6 }}>
            {!isRecipeTagsPending && recipeTags ? (
              <RecipeListFilterCategory categories={recipeTags} />
            ) : null}
          </GridItem>
        )}

        {/* Recipe List */}
        <GridItem
          px={{ base: 2, md: 0 }}
          colSpan={{ base: 1, lg: keyword ? 24 : 18 }}
        >
          <Stack spacing={{ base: 8, lg: 10 }}>
            {notFiltering
              ? recipeSection?.data.map((section) => (
                  <RecipeList
                    title={section.title}
                    key={section.title}
                    data={section.recipes}
                  />
                ))
              : null}
            <InfiniteScrollRecipeList
              title={notFiltering ? 'Koleksi Resep Chef' : ''}
              keyword={keyword}
              category={category}
              tags={tags}
              handleReset={() => {
                router.replace({
                  pathname: router.pathname,
                });
              }}
            />
          </Stack>
        </GridItem>
      </Grid>
    </Box>
  );
};
